import { PaginationMetadata } from 'api/common-schemas';
import { ServerPaginatedTable } from 'components/Table/ServerPaginatedTable';
import { OnChangeFn, SortingState } from '@tanstack/react-table';
import { ColumnDef } from '@tanstack/react-table';
import { UsePaginatedTableReturn } from 'hooks/usePaginatedTable';
import { TokenUserRoles, User } from 'api/users/schemas/Users';
import { AvatarNameCell } from './AvatarNameCell';
import RoleTag from 'screens/RolesPermissions/CustomRoles/RoleTag';
import UserStatusColumn from 'components/UserStatusColumn';
import { plainTextToPhoneNumber } from 'shared/util/util';
import { UsersActions } from './UsersActions';
import { isAfterDate } from '../../components/Table/filters';
import format from 'date-fns/format';

interface ExistingUsersProps<T, M = PaginationMetadata> {
  sortingState: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  paginatedTableProps: Omit<UsePaginatedTableReturn<T, M>, 'data' | 'metadata'>;
  tablePageData: User[];
}

const UserManagementUsersDef: ColumnDef<User>[] = [
  {
    id: 'name',
    header: 'User',
    accessorKey: 'name',
    cell: (row) => {
      const user = row?.row?.original?.name;
      return <AvatarNameCell name={user} />;
    },
  },
  {
    id: 'role',
    header: 'Role',
    accessorKey: 'role',
    cell: (row) => {
      return (
        <RoleTag
          name={row?.row?.original?.roles[0]?.name ?? TokenUserRoles.VIEWER}
          color={row?.row?.original?.roles[0]?.color ?? ''}
        />
      );
    },
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: (row: { row: { original: { status: string } } }) => (
      <UserStatusColumn status={row?.row?.original?.status} />
    ),
  },
  {
    id: 'phone_number',
    header: 'Phone',
    enableSorting: false,
    cell: (row) =>
      row?.row?.original?.phone_number
        ? plainTextToPhoneNumber(row?.row?.original?.phone_number)
        : ' -- ',
    accessorKey: 'phone_number',
  },
  {
    id: 'email',
    header: 'Email',
    accessorKey: 'email',
  },
  {
    id: 'date_added',
    header: 'Date created',
    cell: (row) => {
      const userData = row?.row?.original;
      const dateAdded = userData?.invited_at || userData?.created_at;
      return dateAdded ? format(new Date(dateAdded), 'MM/dd/yyyy') : ' -- ';
    },
    accessorKey: 'invited_at',
    filterFn: isAfterDate,
  },
  {
    header: 'Actions',
    enableSorting: false,
    cell: (cell) => <UsersActions row={cell.row.original} />,
    enableGlobalFilter: false,
  },
];

const ExistingUsers = <ResultData, MetadataType = PaginationMetadata>({
  tablePageData,
  onSortingChange,
  sortingState,
  paginatedTableProps,
}: ExistingUsersProps<ResultData, MetadataType>) => {
  return (
    <ServerPaginatedTable
      data={tablePageData}
      columns={UserManagementUsersDef}
      title="Users"
      sortingState={sortingState}
      onSortingChange={onSortingChange}
      {...paginatedTableProps}
    />
  );
};

export default ExistingUsers;
